import { EmojiButton } from "@jugl-web/domain-resources/common/components/DescriptionBox/EmojiButton";
import {
  RichTextarea,
  RichTextareaHandle,
} from "@jugl-web/domain-resources/common/components/RichTextarea";
import { ToolbarButton } from "@jugl-web/domain-resources/common/components/TextToolbar";
import { TextFormattingBar } from "@jugl-web/domain-resources/tasks-comments/components/CommentsInput/components/TextFormattingBar";
import { ReactComponent as TextFormattingIcon } from "@jugl-web/domain-resources/tasks-comments/components/CommentsInput/icons/text-formatting.svg";
import { UtilityTextInputBase, Switch } from "@jugl-web/ui-kit";
import {
  assignRefs,
  cx,
  htmlToPlainText,
  useFormFieldsValidators,
  useTranslations,
} from "@jugl-web/utils";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { CRMInfoSection } from "../../../../components/CRMInfoSection";
import { EmailSettingsFormValues } from "../EmailSettingsTab/types";

export const AIEmailSignatureSection: React.FC = () => {
  const { t } = useTranslations();
  const { requiredValidator } = useFormFieldsValidators();
  const {
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<EmailSettingsFormValues>();
  const richTextareaRef = useRef<RichTextareaHandle | null>(null);
  const [isTextFormattingBarOpen, setIsTextFormattingBarOpen] = useState(false);

  const isEnabled = useWatch({
    control,
    name: "ai_email_signature_enabled",
  });
  const signature = useWatch({
    control,
    name: "ai_email_signature",
  });
  const isInvalid = !!errors.ai_email_signature;

  useEffect(() => {
    const currentValue = richTextareaRef.current?.saveToHTML() ?? "";

    if (currentValue !== signature) {
      richTextareaRef.current?.readFromHTML(signature);
    }
  }, [signature]);

  useEffect(() => {
    richTextareaRef.current?.setEditable(isEnabled);
  }, [isEnabled]);

  const handleSignatureUpdate = () => {
    setValue(
      "ai_email_signature",
      richTextareaRef.current?.saveToHTML() ?? "",
      { shouldDirty: true }
    );
  };

  return (
    <CRMInfoSection
      title={
        <span className="flex items-center gap-2.5">
          <Controller
            control={control}
            name="ai_email_signature_enabled"
            render={({ field: { value, onChange } }) => (
              <Switch
                isChecked={value}
                onChange={(checked) => {
                  onChange(checked);
                  if (!checked) {
                    clearErrors("ai_email_signature");
                  }
                }}
              />
            )}
          />
          {t({
            id: "client-support-page.ai-email-signature",
            defaultMessage: "AI Email Signature",
          })}
        </span>
      }
      subtitle={t({
        id: "client-support-page.ai-email-signature-description",
        defaultMessage:
          "Indicated signature will be shown at the bottom of all replies sent by AI Agent",
      })}
    >
      <Controller
        control={control}
        name="ai_email_signature"
        rules={{
          validate: (value, formValues) => {
            if (!formValues.ai_email_signature_enabled) {
              return true;
            }

            const { validate } = requiredValidator();

            if (typeof validate === "function") {
              return validate(htmlToPlainText(value), formValues);
            }

            return true;
          },
        }}
        render={({ field: { ref } }) => (
          <UtilityTextInputBase
            isDisabled={!isEnabled}
            isInvalid={isInvalid}
            errorMessage={errors.ai_email_signature?.message}
            classNames={{ wrapperClassName: "w-full" }}
            inputWrapperClassName="flex-col items-stretch"
          >
            <RichTextarea
              ref={assignRefs([richTextareaRef, ref])}
              namespace="ai-email-signature"
              isDisabled={!isEnabled}
              placeholder={t({
                id: "client-support-page.ai-email-signature-placeholder",
                defaultMessage: "Kind regards,\nAI Agent",
              })}
              onEachUpdate={handleSignatureUpdate}
              startSlot={
                <TextFormattingBar
                  isOpen={isTextFormattingBarOpen}
                  onUpdate={handleSignatureUpdate}
                />
              }
              endSlot={
                <div className="flex items-center gap-2.5 px-5 pb-2">
                  <EmojiButton />
                  <ToolbarButton
                    name="Text formatting"
                    icon={
                      <TextFormattingIcon
                        className={
                          isTextFormattingBarOpen
                            ? "stroke-primary-800"
                            : "stroke-dark-700"
                        }
                      />
                    }
                    isActive={isTextFormattingBarOpen}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() =>
                      setIsTextFormattingBarOpen((isOpen) => !isOpen)
                    }
                  />
                </div>
              }
              classes={{
                placeholder:
                  "text-dark-500 font-secondary leading-4 text-base px-3 py-2 whitespace-pre-line",
                contentEditable: cx(
                  "jugl__custom-scrollbar min-h-[120px] overflow-y-auto outline-0 px-5 py-4 font-secondary leading-4 text-base",
                  !isEnabled ? "cursor-not-allowed text-grey" : "text-dark"
                ),
                container: "w-full",
              }}
            />
          </UtilityTextInputBase>
        )}
      />
    </CRMInfoSection>
  );
};
